import React, { useCallback } from 'react'

import classNames from 'clsx'
import PropTypes from 'prop-types'
import { omit } from 'ramda'

import { makeStyles } from '@mui/styles'

import createLogger from '~/src/Lib/Logging'
import {
  BREAKPOINTS,
  EMPTY_ARRAY,
  useControlledStateV2,
  useIsBreakpointDown,
} from '~/src/Lib/Utils'

import Dropdown from './Dropdown'
import ToggleButtons from './ToggleButtons'
import { optionShape, valueShape } from './utils'

const displayName = 'ResponsiveChooser'
const logger = createLogger(displayName)

const omitDropdownProps = omit(['clear', 'fullWidth', 'renderValue', 'superDense'])

const useStyles = makeStyles({
  root: {
    '&$fullWidthDropdown': {
      display: 'flex',
      '& > *:first-child': {
        flex: '1 0 auto',
      },
      '& > *:last-child': {
        flex: '0 2 100%'
      },
    }
  },
  fullWidthDropdown: {},
}, { name: displayName })

export const ResponsiveChooserComponent = props => {
  const {
    breakpoint = 'sm',
    dataTestId = 'aroya-responsive-chooser',
    disabled = false,
    fullWidth = false,
    maxButtons: passedMax = null,
    minSelected = 0,
    multiple = true,
    options,
    selectAll = false,
    selectAllLabel = '',
    value = multiple ? EMPTY_ARRAY : null,
    vertical = false,
    onChange: onChangeProp,
    ...rest
  } = props
  const classes = useStyles(props)
  const isSmall = useIsBreakpointDown('sm', breakpoint)
  let maxButtons = passedMax
  if (maxButtons == null) {
    maxButtons = vertical ? 10 : 5
  }
  const tooManyOptions = options?.length > maxButtons
  const shouldUseDropdown = (isSmall && !vertical) || tooManyOptions
  const passthrough = shouldUseDropdown ? rest : omitDropdownProps(rest)
  const Chooser = shouldUseDropdown ? Dropdown : ToggleButtons

  const [selected, setSelected] = useControlledStateV2({ prop: value, setter: onChangeProp })

  const onChange = useCallback(newSelected => {
    if (multiple && minSelected > newSelected.length) return

    setSelected(newSelected)
  }, [multiple, minSelected, setSelected])

  return (
    <div
      data-testid={dataTestId}
      className={classNames({
        [classes.root]: true,
        [classes.fullWidthDropdown]: shouldUseDropdown && props.fullWidth
      })}
    >
      <Chooser
        {...passthrough}
        disabled={disabled}
        fullWidth={fullWidth}
        multiple={multiple}
        selectAll={selectAll}
        selectAllLabel={selectAllLabel}
        vertical={vertical}
        options={options}
        value={selected}
        onChange={onChange}
      />
      {shouldUseDropdown && props.fullWidth ? <div /> : null}
    </div>
  )
}

ResponsiveChooserComponent.propTypes = {
  breakpoint: PropTypes.oneOf(BREAKPOINTS),
  dataTestId: PropTypes.string,
  disabled: PropTypes.bool,
  fullWidth: PropTypes.bool,
  multiple: PropTypes.bool,
  vertical: PropTypes.bool,
  maxButtons: PropTypes.number,
  minSelected: PropTypes.number,
  options: PropTypes.arrayOf(optionShape).isRequired,
  selectAll: PropTypes.bool,
  selectAllLabel: PropTypes.string,
  value: valueShape,
  onChange: PropTypes.func.isRequired,
}

ResponsiveChooserComponent.displayName = displayName

export default React.memo(ResponsiveChooserComponent)
